import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Container,
  LineContainer,
  Line,
  Property,
  Values,
  Value,
  InnerValue,
  ColorSwatch,
  Punctuation,
} from './CodeBlock.styles'
import { CSStyles, InnerPropertyStyle } from '../../useGenerateLayerCSS'

export interface CodeBlockProps {
  styles: CSStyles
  className?: string
}

export const CodeBlock = ({ styles, className }: CodeBlockProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const innerContainerRef = useRef<HTMLDivElement>(null)
  const [containerStyle, setContainerStyle] = useState({})

  useEffect(() => {
    /**
     * Format clipboard content given our html structure within the code block all elements are copied with a \n
     */
    const formatClipboard = (event: ClipboardEvent) => {
      event.preventDefault()

      const selection = window.getSelection()
      const formattedText = selection
        ?.toString()
        .trim()
        .replace(/\n/g, ' ')
        .split(/\s*;\s*/)
        .join(';\n')

      if (formattedText) {
        navigator.clipboard.writeText(formattedText)
      }
    }

    const div = containerRef.current
    if (div) {
      div.addEventListener('copy', formatClipboard)
    }

    return () => {
      if (div) {
        div.removeEventListener('copy', formatClipboard)
      }
    }
  }, [])

  useEffect(() => {
    const updateContainerHeight = () => {
      if (innerContainerRef.current && innerContainerRef.current) {
        const contentHeight = innerContainerRef.current.offsetHeight
        if (contentHeight > 130) {
          // Set fixed height and enable scroll when content exceeds 130px
          setContainerStyle({
            minHeight: '130px',
            height: '130px',
            overflowY: 'auto',
            resize: 'vertical',
          })
        } else {
          setContainerStyle({
            height: 'auto',
            overflowY: 'visible',
          })
        }
      }
    }

    updateContainerHeight()

    // Re-check on content changes or window resize
    window.addEventListener('resize', updateContainerHeight)

    // Update container styles when content changes
    const observer = new MutationObserver(updateContainerHeight)
    if (innerContainerRef.current) {
      observer.observe(innerContainerRef.current, {
        childList: true,
        subtree: true,
      })
    }

    return () => {
      window.removeEventListener('resize', updateContainerHeight)
      observer.disconnect()
    }
  }, [])

  const renderedStyles = useMemo(() => {
    return styles.map((style, index) => {
      return (
        <LineContainer key={`line-${style.property}-${index}`}>
          <Line>
            <Property>{style.property}:</Property>
            <Values>
              {style.values.map((value, index) => (
                <React.Fragment key={`line-value-${index}`}>
                  {value.innerProperty ? (
                    <StyleValueInnerProperty
                      property={value.innerProperty.property}
                      values={value.innerProperty.values}
                      lastItem={index === style.values.length - 1}
                    />
                  ) : (
                    <StyleValue
                      value={value.value}
                      color={value.color}
                      lastItem={index === style.values.length - 1}
                    />
                  )}
                </React.Fragment>
              ))}
            </Values>
          </Line>
        </LineContainer>
      )
    })
  }, [styles])

  return (
    <Container
      data-container={true}
      ref={containerRef}
      className={className}
      style={containerStyle}
    >
      <div ref={innerContainerRef}>{renderedStyles}</div>
    </Container>
  )
}

interface StyleValueProps {
  value?: string
  color?: string
  lastItem?: boolean
}

function StyleValue({ value, color, lastItem }: StyleValueProps) {
  return (
    <Value>
      {value ? <InnerValue>{value}</InnerValue> : undefined}
      {color ? (
        <>
          <ColorSwatch background={color} spaceLeft={Boolean(value)} />
          <InnerValue>{color}</InnerValue>
        </>
      ) : undefined}

      {lastItem ? <Punctuation>;</Punctuation> : <Punctuation>, </Punctuation>}
    </Value>
  )
}

interface StyleValueInnerPropertyProps {
  property?: string
  values: InnerPropertyStyle[]
  lastItem?: boolean
}

function StyleValueInnerProperty({
  property,
  values,
  lastItem,
}: StyleValueInnerPropertyProps) {
  const innerValues = values.map((iv, index) => {
    const isColorFirst =
      property === 'linear-gradient' ||
      property === 'radial-gradient' ||
      property === 'conic-gradient'

    let content = (
      <>
        {iv.value ? <InnerValue>{iv.value}</InnerValue> : undefined}
        {iv.color ? (
          <>
            <ColorSwatch background={iv.color} spaceLeft={Boolean(iv.value)} />
            <InnerValue>{iv.color}</InnerValue>
          </>
        ) : undefined}
      </>
    )

    if (iv.color && iv.value && isColorFirst) {
      content = (
        <>
          <ColorSwatch background={iv.color} />
          <InnerValue>{iv.color}</InnerValue>
          <InnerValue spaceLeft={true}>{iv.value}</InnerValue>
        </>
      )
    }

    return (
      <Value key={`inner-value-${index}`}>
        {content}
        {index < values.length - 1 ? <Punctuation>, </Punctuation> : undefined}
      </Value>
    )
  })

  return (
    <>
      {property ? <Value>{property}(</Value> : undefined}
      {innerValues}
      {property ? <Value>)</Value> : undefined}
      <Punctuation>{lastItem ? <>;</> : <>, </>}</Punctuation>
    </>
  )
}
